import React, { useEffect, useMemo, useRef } from 'react';

/**
 * Synced lyrics. Takes an LRC (the demo/Suno pipeline produces one per song)
 * and highlights the line at `time`. Falls back to plain scrolling text when a
 * song has no LRC yet, which is the normal state for a freshly added song.
 */

const LRC_LINE = /\[(\d{1,2}):(\d{2})(?:[.:](\d{1,3}))?\]/g;

export function parseLrc(lrc) {
  if (!lrc) return [];
  const lines = [];
  lrc.split(/\r?\n/).forEach((raw) => {
    LRC_LINE.lastIndex = 0;
    const stamps = [];
    let match = LRC_LINE.exec(raw);
    while (match) {
      const [, mm, ss, frac] = match;
      const fraction = frac ? parseFloat(`0.${frac}`) : 0;
      stamps.push(parseInt(mm, 10) * 60 + parseInt(ss, 10) + fraction);
      match = LRC_LINE.exec(raw);
    }
    const text = raw.replace(LRC_LINE, '').trim();
    if (!stamps.length || !text) return;
    stamps.forEach((at) => lines.push({ at, text }));
  });
  return lines.sort((a, b) => a.at - b.at);
}

export default function LyricScroller({ lrc, plain, time = 0, compact = false }) {
  const lines = useMemo(() => parseLrc(lrc), [lrc]);
  const containerRef = useRef(null);
  const activeRef = useRef(null);

  const activeIndex = useMemo(() => {
    if (!lines.length) return -1;
    let index = -1;
    for (let i = 0; i < lines.length; i += 1) {
      if (lines[i].at <= time + 0.15) index = i;
      else break;
    }
    return index;
  }, [lines, time]);

  useEffect(() => {
    const node = activeRef.current;
    const box = containerRef.current;
    if (!node || !box) return;
    // scrollTop rather than scrollIntoView: the latter scrolls the whole page
    // on mobile Safari and yanks the record button out of reach mid-take.
    //
    // Measure with rects, not offsetTop. offsetTop is relative to the nearest
    // *positioned* ancestor, and this panel sits inside a plain static chain —
    // it resolved to .main-content, so a line 300px down the box reported 1129
    // and the panel scrolled somewhere unrelated to the highlight.
    const boxRect = box.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    const offsetInBox = box.scrollTop + (nodeRect.top - boxRect.top);
    const target = offsetInBox - box.clientHeight / 2 + nodeRect.height / 2;
    const limit = box.scrollHeight - box.clientHeight;
    box.scrollTo({ top: Math.max(0, Math.min(target, limit)), behavior: 'smooth' });
  }, [activeIndex]);

  if (!lines.length) {
    return (
      <div className={`gs-lyrics gs-lyrics--plain ${compact ? 'gs-lyrics--compact' : ''}`}>
        {(plain || 'Lyrics coming soon.').split(/\r?\n/).map((line, i) => (
          <p key={i} className={line.trim() ? '' : 'gs-lyrics__gap'}>{line}</p>
        ))}
      </div>
    );
  }

  // Songs open on an instrumental — 27 seconds of it on Tiranga Uthega. With
  // nothing highlighted yet the panel just looks broken, and mid-take a singer
  // has no idea when to come in. Count them in instead.
  const leadIn = activeIndex < 0 ? lines[0].at - time : null;

  return (
    <div className={`gs-lyrics ${compact ? 'gs-lyrics--compact' : ''}`} ref={containerRef}>
      {leadIn > 0 && (
        <p className={leadIn <= 5 ? 'gs-lyrics__leadin is-close' : 'gs-lyrics__leadin'}>
          {leadIn <= 5
            ? `Get ready… ${Math.ceil(leadIn)}`
            : `♪ Singing starts in ${Math.ceil(leadIn)}s`}
        </p>
      )}
      {lines.map((line, index) => (
        <p
          key={`${line.at}-${index}`}
          ref={index === activeIndex ? activeRef : null}
          className={
            index === activeIndex
              ? 'gs-lyrics__line is-active'
              : index === activeIndex + 1
              ? 'gs-lyrics__line is-next'
              : 'gs-lyrics__line'
          }
        >
          {line.text}
        </p>
      ))}
    </div>
  );
}
